import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, TemplateRef } from '@angular/core';
import {Observable, Subscription, timer} from 'rxjs';
import { SearchObjectResult } from '../search-object-result.model';
import { LoaderService } from '../services/loader.service';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { DataFiltersService } from '../data-filters/data-filters.service';
import { DataFiltersComponent } from '../data-filters/data-filters.component';
import { DragulaService } from 'ng2-dragula';
import {finalize, tap} from 'rxjs/operators';
import { ErrorHandlerService } from '../services/error-handler.service';

@Component({
  selector: 'app-data-list',
  templateUrl: './data-list.component.html',
  styleUrls: ['./data-list.component.scss']
})
export class DataListComponent<T> implements OnInit, OnChanges, OnDestroy {

  subs = new Subscription();

  items: any[] = [];

  @Input()
  loadPage: (page: number, pageSize: number) => Observable<SearchObjectResult<T>>;

  @Input()
  draggableName: string;

  @Input()
  preventPageChange: boolean = false;

  @Input()
  infiniteScrollThrottle = 1000;

  @Input()
  filters: any = {};

  @Input()
  filtersBase: any;

  @Input()
  itemTemplate: TemplateRef<any>;

  @Input()
  extraActionFields: TemplateRef<any>;

  @Input()
  draggable: boolean;

  @Input()
  pageSize = 10;

  @Input()
  hidePagination = false;

  @Input()
  infiniteScroll = false;

  @Input()
  infiniteScrollContainer: string;

  @Input()
  filtersComponent: DataFiltersComponent;

  @Input()
  loadAtStartup = true;

  @Output()
  filtersChange = new EventEmitter<any>();

  @Output()
  beforeReset = new EventEmitter<void>();

  @Output()
  onReset = new EventEmitter<void>();

  @Output()
  onLoad = new EventEmitter<SearchObjectResult<T>>();

  @Output()
  onItemsChange = new EventEmitter<T[]>();

  @Output()
  onPageChange = new EventEmitter<number>();

  @Output()
  onItemPositionChanged = new EventEmitter<any>();

  count           = 0;
  totalCount      = 0;
  page            = 1;
  lastPage        = 1;
  pageSizeOptions = [5, 10, 20, 30, 40, 50];


  resetSubscription: Subscription;
  applySubscription: Subscription;

  loading = false;

  constructor(
    private loader: LoaderService,
    private toastr: ToastrService,
    private translate: TranslateService,
    private filtersService: DataFiltersService,
    private dragulaService: DragulaService,
    private errorHandler: ErrorHandlerService,
  ) {

  }

  ngOnInit() {
    if (this.draggable) {
      const itemPositionChange = this.onItemPositionChanged;
      this.subs.add(this.dragulaService.dropModel(this.draggableName)
        .subscribe(({ sourceIndex, targetIndex, sibling, sourceModel, targetModel, item }) => {
          itemPositionChange.emit({ index: targetIndex, item: item, items: targetModel });
        })
      );
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.loadPage && changes.loadPage.currentValue && this.loadAtStartup) {
      this.changePage(1);
    }

    if (changes.filtersComponent && changes.filtersComponent.currentValue) {
      this.resetSubscription = this.filtersComponent.getResetObservable().subscribe(() => this.reset());
      this.applySubscription = this.filtersComponent.getApplyObservable().subscribe(() => this.refresh());
    }
  }

  refresh() {
    this.changePage(1);
  }

  reset() {
    this.beforeReset.emit();

    if (this.filtersBase) {
      this.filters = { ...this.filtersBase };
    } else {
      this.filters = {};
    }

    this.filtersChange.emit(this.filters);
    this.onReset.emit();

    this.refresh();
  }

  changePage(page: number) {
    if (this.preventPageChange) {
      const confirmPrompt =
              confirm('You have unsaved changes. Press Cancel to go back and save these changes, or OK to lose these changes.');
      if (!confirmPrompt) {
        return;
      }
    }
    if (!this.loadPage) {
      return;
    }

    this.page = page;
    this.onPageChange.emit(this.page);

    this.loader.startLoad();

    this.loadPage(this.page, this.pageSize)
      .pipe(finalize(() => this.loader.stopLoad()))
      .subscribe(res => {
        this.count      = res.count;
        this.totalCount = res.totalCount;
        this.items      = res.results;
        this.lastPage   = res.totalPages;

        this.onLoad.emit(res);
        this.onItemsChange.emit(this.items);
      }, e => this.errorHandler.handleHttpError(e, 'data-table.errors.load-page'));
  }

  loadNextPage() {
    if (!this.loadPage) {
      return;
    }

    if (this.loading) {
      console.log('Already loading a page');
      return;
    }

    this.page = this.page + 1;
    this.onPageChange.emit(this.page);

    this.loader.startLoad();
    this.loading = true;

    this.loadPage(this.page, this.pageSize)
      .pipe(finalize(() => {
        this.loader.stopLoad();
        this.loading = false;
      }))
      .subscribe(res => {
        this.items.push(...res.results);

        this.onLoad.emit(res);
        this.onItemsChange.emit(this.items);
      }, e => this.errorHandler.handleHttpError(e, 'data-table.errors.load-page'));
  }

  onScroll() {
    timer(this.infiniteScrollThrottle).pipe(
      tap(() => this.loadNextPage())
    ).subscribe()
  }

  ngOnDestroy() {
    this.resetSubscription.unsubscribe();
    this.applySubscription.unsubscribe();
    this.subs.unsubscribe();
  }

}
